import { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Card, CardContent, CardHeader, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { RootState } from 'store/store'
import DataAnalysisInfo from 'pages/DataAnalysis/components/DataAnalysisInfo'
import { TTrendChartResponseType } from 'types/models/DataAnalysis'
import { getTabByKey, prettyAttr, TAB_KEYS } from '../../utils/consts'
import { MetricBarChart } from 'components/charts'
import DataAnalysisFilters from '../../components/DataAnalysisFilters/DataAnalysisFilters'
import { applyAndFetchChart } from 'store/slices/dataAnalysisSlice'
import {
  selectChartData,
  selectChartStatus,
  selectWheatColorByCodeMap,
} from 'store/selectors/dataAnalysisSelectors'
import EmptyState from 'pages/DataAnalysis/components/EmptyState'
import { toParametersComparisonData } from '../../transforms'
import * as sxStyles from '../tabs.styles'
import { DataTabsLayout, TrendChartStatus } from '../../layouts'
import { ChartExportWrapper } from '../../../../components'

type AnyDataset =
  | TTrendChartResponseType
  | TTrendChartResponseType[]
  | { data: TTrendChartResponseType }
  | null
  | undefined

// Input: Anything matching AnyDataset
// Output: Always one TTrendChartResponseType in a clean, predictable shape.
const normalizeDataset = (raw: AnyDataset): TTrendChartResponseType => {
  if (!raw) return {}

  if (
    typeof raw === 'object' &&
    !Array.isArray(raw) &&
    'data' in (raw as any)
  ) {
    return normalizeDataset((raw as any).data)
  }

  // merge an array of datasets into one
  if (Array.isArray(raw)) {
    return raw.reduce((acc, ds) => {
      Object.entries(ds ?? {}).forEach(([k, v]) => {
        acc[k] = Array.isArray(v) ? v : [] // force array
      })
      return acc
    }, {} as TTrendChartResponseType)
  }

  // coerce every value to array
  const obj = raw as Record<string, unknown>
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [
      k,
      Array.isArray(v) ? v : v != null ? [v] : [],
    ])
  ) as TTrendChartResponseType
}

export const ParametersComparison = () => {
  const dispatch = useDispatch()
  const rawData = useSelector(selectChartData(TAB_KEYS.PARAMETERS_COMPARISON))
  const status = useSelector(selectChartStatus(TAB_KEYS.PARAMETERS_COMPARISON))
  const colorByCode = useSelector(selectWheatColorByCodeMap)
  const units = useSelector((state: RootState) => state?.dataAnalysis?.units)

  const data = useMemo(() => normalizeDataset(rawData as AnyDataset), [rawData])
  const currentTab = getTabByKey(TAB_KEYS.PARAMETERS_COMPARISON)
  // To know if we need to render legend for trendChart
  const hasInfo = !!currentTab?.info?.length

  const charts = useMemo(
    () =>
      Object.entries(data).map(([attr, rows]) => ({
        attr,
        chartData: toParametersComparisonData(rows),
      })),
    [data]
  )
  const hasAnyData = charts.some((c) => c.chartData.length > 0)

  return (
    <DataTabsLayout
      sidebar={
        <DataAnalysisFilters
          filtersList={currentTab?.filters ?? []}
          chartKey={TAB_KEYS.PARAMETERS_COMPARISON}
        />
      }
      tabKey={TAB_KEYS.PARAMETERS_COMPARISON}
      onApply={() =>
        dispatch(applyAndFetchChart({ key: TAB_KEYS.PARAMETERS_COMPARISON }))
      }
    >
      <TrendChartStatus status={status} title={TAB_KEYS.PARAMETERS_COMPARISON}>
        {!hasAnyData ? (
          <EmptyState />
        ) : (
          <Grid container spacing={2}>
            {Object.entries(data).map(([attr, rows]) => {
              const chartData = toParametersComparisonData(rows)
              if (!chartData.length) return null
              if (attr === 'gluten_index') return null

              return chartData.length === 0 ? (
                <Grid key={attr} size={{ xs: 6, md: 6, lg: 6, xl: 6 }} flex={1}>
                  <Typography>There is no data to show</Typography>
                </Grid>
              ) : (
                <Grid key={attr} size={{ xs: 12, md: 6, lg: 6, xl: 6 }}>
                  <Card
                    data-exportable-chart="true"
                    variant="outlined"
                    sx={sxStyles.parametersComparisonCard}
                  >
                    <CardHeader
                      title={prettyAttr(attr)}
                      titleTypographyProps={{ fontSize: 24, fontWeight: 800 }}
                    />
                    <CardContent sx={sxStyles.parametersComparisonCardContent}>
                      <ChartExportWrapper title={prettyAttr(attr)}>
                        <MetricBarChart
                          data={chartData}
                          yUnit={units ? units[prettyAttr(attr)] : ''}
                          height={320}
                          colorByCode={colorByCode}
                        />
                      </ChartExportWrapper>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
            {hasInfo && <DataAnalysisInfo infoList={currentTab.info} />}
          </Grid>
        )}
      </TrendChartStatus>
    </DataTabsLayout>
  )
}
